<template>
  <div class="c-app-home-menu fs">
    <div v-if="!collapse" class="menus__header">
      <div class="menus__title">收藏夹</div>
      <div class="menus__extra">
        <ExtraMoreAction />
      </div>
    </div>
    <div class="menus__wrapper">
      <el-menu
        class="menus"
        :unique-opened="false"
        :default-active="activedMenuId"
        :default-openeds="openedMenuIds"
        :collapse="collapse"
        background-color="#0e1524"
        text-color="#909399"
        active-text-color="#fff"
        @select="handleSelect"
        @open="handleOpen"
      >
        <template v-for="menu in rootMenus" :key="menu.id">
          <menu-folder v-if="menu?.children?.length > 0 && !collapse" :menu="menu" :collapse="collapse"></menu-folder>
          <menu-folder-collapse v-else-if="menu?.children?.length > 0 && collapse" :menu="menu" />
          <menu-item v-else :menu="menu" :collapse="collapse"></menu-item>
        </template>
      </el-menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  _,
  computed,
  ref,
  reactive,
  provide,
  watch,
  onMounted,
  onBeforeRouteUpdate,
  useHostAppInstance
} from '@zto/zpage'
import type { NavMenuItem } from '@zto/zpage'

import { ElementPlusIcons } from '@zto/zpage-ui-element'

import MenuFolder from './menu-folder.vue'
import MenuFolderCollapse from './menu-folder-collapse.vue'
import MenuItem from './menu-item.vue'
import ExtraMoreAction from './extra-more-action.vue'

import { addFavFolder, fetchFavItems, removeFavItem } from '../../../../utils/fav'

import { APP_HOME_MENU_KEY } from './util'

const { Folder: FolderIcon } = ElementPlusIcons

const props = withDefaults(
  defineProps<{
    collapse?: boolean
  }>(),
  {
    collapse: false
  }
)

const app = useHostAppInstance()

provide(APP_HOME_MENU_KEY, {
  remove,
  rename,
  addChild
})

const { userStore, pagesStore } = app.stores
const router = app.router
const route = app.useRoute()

const isPortal = app.isPortal

// 最多导航条
const maxNavs = app.useAppConfig('menu.maxNavs', 10)
const { Message } = app.useMessage()

const rootMenus = ref<any[]>([])

const openedMenuIds = ref<Array<string>>([])
const activedMenuId = ref<string>(null)

const userFavorite = computed(() => {
  return userStore.data.favorite || {}
})

const existsFavs = computed(() => {
  return userFavorite.value.favs || []
})

watch(
  () => userFavorite.value.favs,
  () => {
    fetchRootMenus()
  }
)

onMounted(async () => {
  await fetchRootMenus()

  // 暂不支持刷新切换菜单
  // _onRouteChange(route)
})

onBeforeRouteUpdate(async (to, from) => {
  // _onRouteChange(to)
})

function handleOpen() {}

/**
 * 选中菜单时触发
 */
function handleSelect(index: number) {
  const favMenu = existsFavs.value.find(it => it.id === index)

  if (!favMenu) return

  const menuBizId = favMenu.itemCode

  const route = app.router.getRoutes().find(it => it?.meta.bizId === menuBizId)
  if (!route) {
    Message.warning(`当前页面不存在，请联系管理员！`)
    return
  }

  const meta = route?.meta
  const path = route?.path

  if (meta?.cache !== false) {
    const navPages = pagesStore.navPages
    if (navPages && navPages.length >= maxNavs) {
      Message.warning(`至多打开${maxNavs}个页面,请关闭不使用的页面后再次尝试！`)
      return
    }
  }

  // 使用setTimeout防止菜单卡顿
  setTimeout(() => {
    // 如果配置的是外链，则新开标签页
    if (typeof path === 'string' && (path.startsWith('/http:') || path.startsWith('/https:'))) {
      window.open(path.slice(1))
      return
    }

    // 不能用goto，否则可能导致切换menu后任然缓存页面
    router.push({
      name: route.name,
      query: { ...meta?.query, __fav: '1' }
    })
  }, 5)
}

/**
 * 重命名
 * @param mane
 * @param newName
 */
async function rename(menu: any) {
  const result = await app.ui.showMessageBox({
    boxType: 'prompt',
    title: '重命名',
    message: '请输入新的文件夹名称',
    inputPlaceholder: '请输入，最多10个字',
    inputValue: menu.folderName || menu.itemName,
    inputValidator: function (val: any) {
      if (!val) return '请输入'
      if (val.length > 10) return '不超过10个字'
      return true
    }
  })

  const newName = result.value

  if (!newName) return

  if (menu.isFolder) {
    menu.folderName = newName
  } else {
    menu.itemName = newName
  }

  await app.ui.showMessage({
    type: 'success',
    message: '重命名成功'
  })
}

/**
 * 执行删除
 * @param menu
 */
async function remove(menu: any) {
  await app.ui.showMessageBox({
    boxType: 'confirm',
    title: '提示',
    message: '确认要删除该收藏吗？'
  })

  await removeFavItem(app, menu.id)

  const pMenu = findParentMenuById(menu.id)

  if (!pMenu) return

  const childMenus = pMenu.isRoot ? rootMenus.value : pMenu.children
  const newChildMenus = childMenus.filter((it: any) => it.id !== menu.id)

  if (pMenu.isRoot) {
    rootMenus.value = newChildMenus
  } else {
    pMenu.children = newChildMenus
  }

  await app.ui.showMessage({
    type: 'success',
    message: '删除收藏成功'
  })
}

/**
 * 新建子文件夹
 * @param menu
 */
async function addChild(pMenu: any) {
  const result = await app.ui.showMessageBox({
    boxType: 'prompt',
    title: '新建子文件夹',
    message: '请输入子文件夹名称',
    inputPlaceholder: '请输入，最多10个字',
    inputValidator: function (val: any) {
      if (!val) return '请输入'
      if (val.length > 10) return '不超过10个字'
      return true
    }
  })

  const newName = result.value
  if (!newName) return

  const pid = pMenu.isRoot ? -1 : pMenu.id

  await addFavFolder(app, pid, newName)

  if (pMenu.isRoot) {
    await fetchRootMenus()
  } else {
    pMenu.children = await fetchFavItems(app, pid)

    if (!openedMenuIds.value.includes(pid)) {
      openedMenuIds.value = [...openedMenuIds.value, pid]
    }
  }

  await app.ui.showMessage({
    type: 'success',
    message: '新增文件夹成功'
  })
}

/**
 * 获取根菜单
 */
async function fetchRootMenus() {
  rootMenus.value = await fetchFavItems(app, -1)
}

// 根据id找到当前菜单的父节点
function findParentMenuById(id: number, pMenu?: any) {
  // 查询跟目录
  if (!pMenu) {
    if (rootMenus.value.some(it => it.id === id)) {
      return { isRoot: true }
    }
  }

  let childrenMenus: any[] = []

  if (!pMenu) {
    childrenMenus = rootMenus.value
  } else {
    childrenMenus = pMenu.children || []
  }

  for (let it of childrenMenus) {
    if (!it) continue

    if (it.id === id) {
      return pMenu
    }

    const _pMenu = findParentMenuById(id, it)

    if (_pMenu) {
      return _pMenu
    }
  }

  return null
}

/**
 * 路由改变时触发
 */
function _onRouteChange(route: any) {
  if (!router.query.__fav) return

  const menuName = String(route.name)

  const favMenu = existsFavs.value.find(it => it.itemCode === menuName)

  if (!favMenu) {
    openedMenuIds.value = []
    activedMenuId.value = null
  } else {
    activedMenuId.value = favMenu.id
    openedMenuIds.value = _getParentMenuNames(favMenu, existsFavs.value, [])
  }
}

// 获取当前路由所有父节点名称
function _getParentMenuNames(menu: NavMenuItem, allMenus: NavMenuItem[], cached: string[]) {
  if (!menu.parentId) return cached

  const parentMenu = allMenus.find(it => it.id === menu.parentId)
  if (!parentMenu) return cached

  cached.push(parentMenu.name)

  _getParentMenuNames(parentMenu, allMenus, cached)

  return cached
}
</script>

<style lang="scss" scoped>
.c-app-home-menu {
  width: 100%;
  height: 100%;
  padding-top: 10px;
  border-right: 0;
}

.menus {
  &__header {
    color: white;
    display: flex;
    padding: 10px;
  }

  &__title {
    flex: 1;
    font-size: 16px;
  }

  &__extra {
    padding-right: 15px;
    .action__icon {
      cursor: pointer;
    }
  }
}

:deep(.menu-item),
:deep(.menu-folder > .el-sub-menu__title) {
  &:hover {
    background: #38486d !important;

    & > .menu-title,
    & > i {
      color: var(--primary-color);

      i {
        color: var(--primary-color);
      }
    }
  }
}

:deep(.el-menu) {
  border-right: 0;

  .el-menu-item {
    height: 40px;
    line-height: 40px;
    min-width: calc(100% - 1px);
    font-size: 14px;

    .menu-title {
      flex: 1;
    }

    &.is-active {
      background: url(/images/menu-bg.png) left top no-repeat;

      .img-icon {
        opacity: 1;
      }
    }
  }

  .el-sub-menu {
    .el-menu-item {
      min-width: 100px;
      padding-right: 20px;
    }
  }

  .el-sub-menu__title {
    display: flex;
    align-items: center;
    font-size: 14px;
    height: 50px;
    line-height: 50px;

    .menu-title {
      flex: 1;
    }

    * {
      vertical-align: initial;
    }
  }
}
</style>
